import type ReportingUiScopeMathsDashboardIndexRoute from 'teacher-ui-client/reporting/ui-scope/maths/dashboard/index/route'
import { t, type IntlService } from 'ember-intl'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingDashboardControls } from 'teacher-ui-client/components/reporting/dashboard-controls'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { array } from '@ember/helper'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { ReportingDashboardDynamic } from 'teacher-ui-client/components/tiles/reporting-dashboard/dynamic'
import { DashboardTotalUsageOverTime } from 'teacher-ui-client/components/giraffe/charts/dashboard/total-usage-over-time'
import { not } from 'ember-truth-helpers'
import { LinkTo } from '@ember/routing'
import { ReportingDashboardStatic } from 'teacher-ui-client/components/tiles/reporting-dashboard/static'
import { DashboardCourseProgress } from 'teacher-ui-client/components/giraffe/charts/dashboard/course-progress'
import { DashboardAssessmentScores } from 'teacher-ui-client/components/giraffe/charts/dashboard/assessment-scores'
import { DashboardWorkingAtGradeLevel } from 'teacher-ui-client/components/giraffe/charts/dashboard/working-at-grade-level'
import { DashboardSkillsMasteredMs } from 'teacher-ui-client/components/giraffe/charts/dashboard/skills-mastered'
import { DashboardMentalMinute } from 'teacher-ui-client/components/giraffe/charts/dashboard/mental-minute'
import { DashboardDrivingTests } from 'teacher-ui-client/components/giraffe/charts/dashboard/driving-tests'
import { AwardsEarnedRibbons } from 'teacher-ui-client/components/charts/dashboard/awards-earned/ribbons'
import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type MathsDrivingTestsService from 'teacher-ui-client/services/maths-driving-tests'
import type RouterService from '@ember/routing/router-service'
import { themeKeys as statBoxThemes } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import { isPresent } from '@ember/utils'

interface Signature {
  Args: {
    model: ModelFor<ReportingUiScopeMathsDashboardIndexRoute>
  }
}

export class UiScopeMathsDashboardIndexPage extends Component<Signature> {
  @service intl!: IntlService

  @service mathsDrivingTests!: MathsDrivingTestsService

  @service router!: RouterService

  readonly routePrefix = 'reporting.ui-scope.maths.dashboard'

  get totalUsageBoxes() {
    const { intl } = this
    return [
      {
        category: 'lessons',
        theme: statBoxThemes.THEME_FOREST_GREEN,
      },
      {
        category: 'quizzes',
        theme: statBoxThemes.THEME_GRAPEY_GREEN,
        tooltip: intl.t('reporting.totalUsageOverTime.tooltips.quizzes'),
      },
      {
        category: 'driving_tests',
        theme: statBoxThemes.THEME_OCEANY_BLUE,
        tooltip: intl.t('reporting.totalUsageOverTime.tooltips.drivingTests'),
      },
      {
        category: 'assessments',
        theme: statBoxThemes.THEME_JUICY_ORANGE,
        tooltip: intl.t('reporting.totalUsageOverTime.tooltips.assessments'),
      },
      {
        category: 'mental_minute',
        theme: statBoxThemes.THEME_MID_ORANGE,
        tooltip: intl.t('reporting.totalUsageOverTime.tooltips.mentalMinute'),
      },
    ]
  }

  get isSummaryDataPresent() {
    return isPresent(this.args.model.totalUsageOverTime)
  }

  get drivingTestGradeNames() {
    return this.mathsDrivingTests.drivingTestData.reduce((mapping, dtData) => {
      return { ...mapping, [dtData.drivingTestGrade]: dtData.name }
    }, {})
  }

  goToCategory = (category: PropertyKey) => {
    const paths: Record<PropertyKey, string | undefined> = {
      lessons: 'course-progress.lessons',
      quizzes: 'skills-mastered.cohort.students-results',
      assessments: 'assessment-scores',
      driving_tests: 'driving-tests',
      mental_minute: 'fluency-sprints.teacher',
    }
    const path = paths[category]

    if (path) void this.router.transitionTo(`reporting.ui-scope.maths.${path}`)
  }

  <template>
    <div class="mx-4">
      <ReportingTeacherUiHeader @title={{t "reporting.dashboardTitles.maths"}} />
      <span class="float-right print:hidden">
        <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-3 w-3 cursor-pointer" />
        <Tooltip @text={{t "reporting.dashboardTooltip"}} />
      </span>

      <ReportingDashboardControls
        @students={{@model.scopedStudents}}
        @routePrefix={{this.routePrefix}}
        @exportOptions={{array ExportType.print}}
        @isDataPresent={{this.isSummaryDataPresent}}
        @studentRouteName="reporting.ui-scope.maths.student-summary"
      />

      <ReportingDashboardDynamic
        data-test-total-usage-row
        class="page-break-after-always mb-4"
        @empty={{not this.isSummaryDataPresent}}
        @title={{t "reporting.chartTitles.dashboard.totalUsageOverTime"}}
        @tooltip={{t "reporting.tooltips.dashboard.totalUsageOverTime"}}
      >
        {{#if @model.totalUsageOverTime}}
          <DashboardTotalUsageOverTime
            class="h-auto"
            @product={{@model.product}}
            @boxes={{this.totalUsageBoxes}}
            @data={{@model.totalUsageOverTime}}
            @tileClickAction={{this.goToCategory}}
            @uiScope={{@model.uiScope.scope}}
          />
        {{/if}}
      </ReportingDashboardDynamic>

      <div class="grid auto-rows-[26rem] grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 print:grid-cols-3">
        <LinkTo @route="reporting.ui-scope.maths.course-progress.lessons">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.courseProgressByGrade}}
            @title={{t "reporting.chartTitles.dashboard.courseProgressByGrade.maths"}}
            @tooltip={{t "reporting.tooltips.dashboard.courseProgressByGrade.maths"}}
          >
            {{#if @model.courseProgressByGrade}}
              <DashboardCourseProgress
                data-test-course-progress-by-grade
                @data={{@model.courseProgressByGrade}}
                @product={{@model.product}}
                @precinct="lessons"
              />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo @route="reporting.ui-scope.maths.assessment-scores">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.assessmentScores}}
            @title={{t "reporting.chartTitles.dashboard.assessmentScores.maths"}}
            @tooltip={{t "reporting.tooltips.dashboard.assessmentScores.maths"}}
          >
            {{#if @model.assessmentScores}}
              <DashboardAssessmentScores data-test-assessment-scores @data={{@model.assessmentScores}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <ReportingDashboardStatic
          @empty={{not @model.workingAtGradeLevel}}
          @title={{t "reporting.chartTitles.dashboard.workingAtGradeLevel"}}
          @tooltip={{t "reporting.tooltips.dashboard.workingAtGradeLevel"}}
        >
          {{#if @model.workingAtGradeLevel}}
            <DashboardWorkingAtGradeLevel data-test-working-at-grade-level @data={{@model.workingAtGradeLevel}} />
          {{/if}}
        </ReportingDashboardStatic>

        <LinkTo @route="reporting.ui-scope.maths.skills-mastered.cohort.students-results">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.skillsMastered}}
            @title={{t "reporting.chartTitles.dashboard.skillsMastered"}}
            @tooltip={{t "reporting.tooltips.dashboard.skillsMastered.maths"}}
          >
            {{#if @model.skillsMastered}}
              <DashboardSkillsMasteredMs @data={{@model.skillsMastered}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo @route="reporting.ui-scope.maths.fluency-sprints.teacher">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.mentalMinuteSummary}}
            @title={{t "reporting.chartTitles.dashboard.mentalMinuteSummary"}}
            @tooltip={{t "reporting.tooltips.dashboard.mentalMinuteSummary"}}
          >
            {{#if @model.mentalMinuteSummary}}
              <DashboardMentalMinute @data={{@model.mentalMinuteSummary}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo @route="reporting.ui-scope.maths.driving-tests">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.drivingTests}}
            @title={{t "reporting.chartTitles.dashboard.drivingTests"}}
            @tooltip={{t "reporting.tooltips.dashboard.drivingTests"}}
          >
            {{#if @model.drivingTests}}
              <DashboardDrivingTests
                data-test-driving-tests
                @data={{@model.drivingTests}}
                @gradeToShortNameMap={{this.drivingTestGradeNames}}
              />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>

        <LinkTo data-test-awards-earned @route="reporting.ui-scope.maths.awards-earned">
          <ReportingDashboardStatic
            class="h-full"
            @empty={{not @model.awardsEarned}}
            @title={{t "reporting.chartTitles.dashboard.awardsEarned.maths"}}
            @tooltip={{t "reporting.tooltips.dashboard.awardsEarned.maths" htmlSafe=true}}
          >
            {{#if @model.awardsEarned}}
              <AwardsEarnedRibbons class="h-full" @data={{@model.awardsEarned}} @withLegend={{true}} />
            {{/if}}
          </ReportingDashboardStatic>
        </LinkTo>
      </div>
    </div>
  </template>
}

export default RouteTemplate(UiScopeMathsDashboardIndexPage)
